import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Robot, Plus, PencilSimple } from "@phosphor-icons/react";

const EMPTY = { name: "", description: "", capabilities: "", availability: "available", enabled: true };

export default function AIAgents() {
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/agents?include_disabled=1");
      setAgents(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, description: a.description || "", capabilities: (a.capabilities || []).join(", "), availability: a.availability, enabled: a.enabled });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        capabilities: form.capabilities.split(",").map((s) => s.trim()).filter(Boolean),
        availability: form.availability,
        enabled: form.enabled,
      };
      if (editing) {
        await api.patch(`/agents/${editing.agent_id}`, payload);
        toast.success("Agent updated");
      } else {
        await api.post("/agents", payload);
        toast.success("Agent created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleEnabled = async (a) => {
    try {
      await api.patch(`/agents/${a.agent_id}`, { enabled: !a.enabled });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-ai-agents">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Robot size={20} className="text-[#E1252A]" />
          <div>
            <div className="text-sm font-semibold">AI Agents Registry</div>
            <div className="text-xs text-zinc-500">Curate autonomous agents that anyone can assign tasks to.</div>
          </div>
        </div>
        <Button onClick={openCreate} className="rounded-sm bg-foreground text-background" data-testid="add-agent-btn">
          <Plus size={14} className="mr-1" /> Add agent
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {agents.map((a) => (
          <div key={a.agent_id} className="border border-border p-4 bg-background" data-testid={`agent-card-${a.agent_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{a.name}</span>
                  <Badge variant="outline" className={`rounded-sm text-[10px] uppercase ${a.availability === "available" ? "text-emerald-600 border-emerald-300" : "text-amber-600 border-amber-300"}`}>
                    {a.availability}
                  </Badge>
                  {!a.enabled && <Badge variant="outline" className="rounded-sm text-[10px] uppercase text-zinc-400">Disabled</Badge>}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{a.description}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={a.enabled} onCheckedChange={() => toggleEnabled(a)} data-testid={`agent-enabled-${a.agent_id}`} />
                <button onClick={() => openEdit(a)} className="h-8 w-8 inline-flex items-center justify-center rounded-sm hover:bg-zinc-100 dark:hover:bg-zinc-900" data-testid={`edit-agent-${a.agent_id}`}>
                  <PencilSimple size={14} />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(a.capabilities || []).map((c) => (
                <Badge key={c} variant="outline" className="rounded-sm font-mono text-[10px] uppercase tracking-wider">{c}</Badge>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="agent-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit agent" : "Add AI agent"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-sm" data-testid="agent-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="rounded-sm" data-testid="agent-desc-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Capabilities (comma separated)</Label>
              <Input value={form.capabilities} onChange={(e) => setForm((f) => ({ ...f, capabilities: e.target.value }))} className="rounded-sm" placeholder="technical writing, analytics" data-testid="agent-caps-input" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.availability === "available"} onCheckedChange={(v) => setForm((f) => ({ ...f, availability: v ? "available" : "busy" }))} data-testid="agent-availability-switch" />
                Available
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="agent-enabled-switch" />
                Enabled
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy || !form.name.trim()} className="rounded-sm bg-foreground text-background" data-testid="save-agent-btn">
              {busy ? "Saving…" : editing ? "Save changes" : "Create agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
